import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { usePartnerFormStore } from "../store/usePartnerFormStore";
import { Container, Button, Card, CardContent } from "./ui";
import { getPartnerFormById } from "../apis/partnerForm";
import { 
  CheckCircle, 
  MapPin, 
  CreditCard, 
  Award,
  Calendar,
  User,
  Briefcase,
  TrendingUp,
  AlertCircle
} from "lucide-react";

interface FormDetails {
  _id: string;
  basicInfo: {
    fullName: string;
    businessName: string;
    mobileNumber: string;
    whatsappNumber?: string;
    employeeCode?: string;
  };
  serviceDetails: {
    category: {
      id: string;
      name: string;
    };
    subcategory: string[];
    serviceChargeCategory: string;
  };
  coverageAreas?: {
    selectedPlan?: {
      hexCount: number;
      pricePerYear: number;
      planName: string;
      isAllKerala: boolean;
    } | null;
    selectedHexagons?: string[];
  } | null;
  additionalInfo: {
    additionalWorkQuantity?: number;
    incomeSharingPercentage: number;
  };
  payment: {
    amount: number;
    currency: string;
    status: string;
    paidAt: string;
    razorpayOrderId?: string;
    razorpayPaymentId?: string;
  };
  selectedPlan?: {
    planId: string;
    planName: string;
    displayName: string;
    pricing: {
      basePrice: number;
      sellingPrice: number;
      finalPrice: number;
    };
    isTrial: string;
    trialDays: number;
    features?: {
      hexCount: number;
      coverageAreaKm2: number;
    };
  } | null;
  subscriptionDetails?: {
    planHistoryId: string;
    startDate: string;
    endDate: string;
    status: string;
    subscriptionType: string;
    autoRenew: boolean;
  } | null;
  partnerId?: string | null;
  status: string;
  createdAt: string;
}

const PartnerFormSuccess: React.FC = () => {
  const { submittedFormId, resetForm } = usePartnerFormStore();
  const [formDetails, setFormDetails] = useState<FormDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFormDetails = async () => {
      if (!submittedFormId) {
        setTimeout(() => {
          navigate("/");
        }, 3000);
        return;
      }

      try {
        const response = await getPartnerFormById(submittedFormId);
        setFormDetails(response);
      } catch (err) {
        console.error("Error fetching form details:", err);
        setError("Could not load registration details. Please contact support.");
      }
    };

    fetchFormDetails();
  }, [submittedFormId, navigate]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (error || (!formDetails && !submittedFormId)) {
    return (
      <div className="min-h-screen bg-secondary-50 py-6 sm:py-8 md:py-12 px-4">
        <Container>
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardContent className="text-center py-12">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
                  <AlertCircle className="h-8 w-8 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-secondary-900 mb-3">
                  {error || "No Registration Found"}
                </h2>
                <p className="text-secondary-600 mb-6">
                  {error || "We couldn't find your registration details. Please contact support."}
                </p>
                <Button onClick={resetForm} variant="primary">
                  Start New Registration
                </Button>
              </CardContent>
            </Card>
          </div>
        </Container>
      </div>
    );
  }

  if (!formDetails) {
    return (
      <div className="min-h-screen bg-secondary-50 py-6 sm:py-8 md:py-12 px-4">
        <Container>
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardContent className="text-center py-12">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-yellow-100 mb-6">
                  <AlertCircle className="h-8 w-8 text-yellow-600" />
                </div>
                <h2 className="text-2xl font-bold text-secondary-900 mb-3">Loading...</h2>
                <p className="text-secondary-600">Please wait while we load your registration details.</p>
              </CardContent>
            </Card>
          </div>
        </Container>
      </div>
    );
  }

  const data = formDetails;
  const isTrial = data.selectedPlan?.isTrial === "yes";
  
  // Safe access with optional chaining and fallbacks
  const coverageAreas = data.coverageAreas || null;
  const selectedPlanData = coverageAreas?.selectedPlan || null;
  const selectedHexagons = coverageAreas?.selectedHexagons || [];
  const coverageArea = selectedPlanData ? selectedPlanData.hexCount * 5.16 : 0;
  
  // Safe access for selectedPlan pricing
  const selectedPlanPricing = data.selectedPlan?.pricing || { basePrice: 0, sellingPrice: 0, finalPrice: 0 };

  return (
    <div className="min-h-screen bg-secondary-50 py-6 sm:py-8 md:py-12 px-4">
      <Container>
        <div className="max-w-4xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-6">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-secondary-900 mb-2">
              Registration Successful!
            </h1>
            <p className="text-base text-secondary-600">
              Thank you, {data.basicInfo.fullName}, for completing the partner registration.
            </p>
            <p className="text-sm text-secondary-500 mt-1">
              Registration ID: {data._id}
            </p>
          </div>

          {/* Registration Details */}
          <div className="space-y-4">
            {/* Basic Information */}
            <Card>
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <User className="h-5 w-5 text-primary-600" />
                  <h2 className="text-lg font-semibold text-secondary-900">Basic Information</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-secondary-500">Full Name</p>
                    <p className="font-medium text-secondary-900">{data.basicInfo.fullName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-secondary-500">Business Name</p>
                    <p className="font-medium text-secondary-900">{data.basicInfo.businessName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-secondary-500">Mobile Number</p>
                    <p className="font-medium text-secondary-900">{data.basicInfo.mobileNumber}</p>
                  </div>
                  {data.basicInfo.whatsappNumber && (
                    <div>
                      <p className="text-xs text-secondary-500">WhatsApp Number</p>
                      <p className="font-medium text-secondary-900">{data.basicInfo.whatsappNumber}</p>
                    </div>
                  )}
                  {data.basicInfo.employeeCode && (
                    <div>
                      <p className="text-xs text-secondary-500">Employee Code</p>
                      <p className="font-medium text-secondary-900">{data.basicInfo.employeeCode}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Service Details */}
            <Card>
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Briefcase className="h-5 w-5 text-primary-600" />
                  <h2 className="text-lg font-semibold text-secondary-900">Service Details</h2>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-secondary-500">Category</p>
                    <p className="font-medium text-secondary-900">{data.serviceDetails.category.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-secondary-500">Subcategories</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {data.serviceDetails.subcategory.map((sub, idx) => (
                        <span key={idx} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {sub}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-secondary-500">Service Charge Category</p>
                    <p className="font-medium text-secondary-900 capitalize">{data.serviceDetails.serviceChargeCategory}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Coverage Plan - With null checks */}
            <Card>
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="h-5 w-5 text-primary-600" />
                  <h2 className="text-lg font-semibold text-secondary-900">Coverage Plan</h2>
                </div>
                {selectedPlanData ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-secondary-500">Selected Plan</p>
                        <p className="font-medium text-secondary-900">{selectedPlanData.planName}</p>
                      </div>
                      <div>
                        <p className="text-xs text-secondary-500">Hexagons</p>
                        <p className="font-medium text-secondary-900">{selectedPlanData.hexCount} hexagons</p>
                      </div>
                      <div>
                        <p className="text-xs text-secondary-500">Coverage Area</p>
                        <p className="font-medium text-secondary-900">
                          {selectedPlanData.isAllKerala ? "Full Kerala" : `~${coverageArea.toFixed(0)} km²`}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-secondary-500">Selected Locations</p>
                        <p className="font-medium text-secondary-900">{selectedHexagons.length} hexagons selected</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-secondary-500">No coverage plan selected</p>
                )}
              </CardContent>
            </Card>

            {/* Plan Subscription Details */}
            {data.selectedPlan && data.selectedPlan.pricing && (
              <Card>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Award className="h-5 w-5 text-primary-600" />
                    <h2 className="text-lg font-semibold text-secondary-900">Subscription Details</h2>
                  </div>
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-secondary-500">Plan</p>
                        <p className="font-medium text-secondary-900">{data.selectedPlan.displayName}</p>
                      </div>
                      <div>
                        <p className="text-xs text-secondary-500">Plan Type</p>
                        <p className="font-medium text-secondary-900 capitalize">
                          {isTrial ? `Trial (${data.selectedPlan.trialDays} days)` : "Annual"}
                        </p>
                      </div>
                      {!isTrial && (
                        <>
                          <div>
                            <p className="text-xs text-secondary-500">Base Price</p>
                            <p className="font-medium text-secondary-900">{formatCurrency(selectedPlanPricing.basePrice)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-secondary-500">GST (18%)</p>
                            <p className="font-medium text-secondary-900">{formatCurrency(selectedPlanPricing.finalPrice - selectedPlanPricing.sellingPrice)}</p>
                          </div>
                        </>
                      )}
                      <div>
                        <p className="text-xs text-secondary-500">Amount Paid</p>
                        <p className="font-bold text-lg text-primary-600">{formatCurrency(selectedPlanPricing.finalPrice)}</p>
                      </div>
                    </div>

                    {data.subscriptionDetails && (
                      <div className="mt-3 pt-3 border-t border-secondary-100">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-secondary-500">Subscription Start</p>
                            <p className="font-medium text-secondary-900">{formatDate(data.subscriptionDetails.startDate)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-secondary-500">Subscription End</p>
                            <p className="font-medium text-secondary-900">{formatDate(data.subscriptionDetails.endDate)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-secondary-500">Subscription Status</p>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {data.subscriptionDetails.status}
                            </span>
                          </div>
                          <div>
                            <p className="text-xs text-secondary-500">Auto Renewal</p>
                            <p className="font-medium text-secondary-900">{data.subscriptionDetails.autoRenew ? "Enabled" : "Disabled"}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Payment Information */}
            <Card>
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <CreditCard className="h-5 w-5 text-primary-600" />
                  <h2 className="text-lg font-semibold text-secondary-900">Payment Information</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-secondary-500">Amount Paid</p>
                    <p className="font-bold text-lg text-primary-600">{formatCurrency(data.payment.amount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-secondary-500">Currency</p>
                    <p className="font-medium text-secondary-900">{data.payment.currency}</p>
                  </div>
                  <div>
                    <p className="text-xs text-secondary-500">Payment Status</p>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {data.payment.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-secondary-500">Payment Date</p>
                    <p className="font-medium text-secondary-900">{formatDate(data.payment.paidAt)}</p>
                  </div>
                  {data.payment.razorpayOrderId && (
                    <div>
                      <p className="text-xs text-secondary-500">Order ID</p>
                      <p className="font-mono text-xs text-secondary-600">{data.payment.razorpayOrderId}</p>
                    </div>
                  )}
                  {data.payment.razorpayPaymentId && (
                    <div>
                      <p className="text-xs text-secondary-500">Payment ID</p>
                      <p className="font-mono text-xs text-secondary-600">{data.payment.razorpayPaymentId}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Additional Information */}
            <Card>
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="h-5 w-5 text-primary-600" />
                  <h2 className="text-lg font-semibold text-secondary-900">Additional Information</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-secondary-500">Income Sharing Percentage</p>
                    <p className="font-medium text-secondary-900">{data.additionalInfo.incomeSharingPercentage}%</p>
                  </div>
                  {data.additionalInfo.additionalWorkQuantity && (
                    <div>
                      <p className="text-xs text-secondary-500">Additional Work Quantity</p>
                      <p className="font-medium text-secondary-900">{data.additionalInfo.additionalWorkQuantity}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-secondary-500">Application Status</p>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      {data.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-secondary-500">Registration Date</p>
                    <p className="font-medium text-secondary-900">{formatDate(data.createdAt)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* What's Next */}
          <div className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="h-5 w-5 text-blue-600" />
              <p className="text-sm sm:text-base text-blue-800 font-semibold">
                What's Next?
              </p>
            </div>
            <ul className="text-sm sm:text-base text-blue-700 space-y-1.5 sm:space-y-2 list-disc list-inside ml-2">
              <li>Our team will review your registration within 24-48 hours</li>
              <li>You will receive login credentials via SMS/Email to access your partner dashboard</li>
              <li>Once approved, you can create and manage service requests</li>
              <li>Track your subscription status and payments from your dashboard</li>
              <li>For any assistance, contact our support team at support@setgoinnovations.com</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Button onClick={resetForm} variant="outline" className="w-full sm:w-auto">
              Submit Another Registration
            </Button>
            <Link to="/login">
              <Button variant="secondary" className="w-full sm:w-auto">
                Partner Login
              </Button>
            </Link>
            <a
              href="https://setgoinnovations.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="primary" className="w-full sm:w-auto">
                Visit Setgo Innovations
              </Button>
            </a>
          </div>

          {/* Footer Note */}
          <div className="mt-6 text-center">
            <p className="text-xs text-secondary-400">
              This is a system generated confirmation. Please save your Registration ID for future reference.
            </p>
            <p className="text-xs text-secondary-400 mt-1">
              For any queries, contact: support@setgoinnovations.com | +91-XXXXXXXXXX
            </p>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default PartnerFormSuccess;